import styled from '@emotion/styled'
import React, { useCallback, useRef, useState } from 'react'
import { Hugeicon } from '../../../icons/Hugeicon'

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  gap: 8px;
`

const TextArea = styled.textarea`
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  min-height: 38px;
  max-height: 120px;
  line-height: 1.4;
  outline: none;

  &:focus {
    border-color: #102638;
    box-shadow: 0 0 0 2px rgba(16, 38, 56, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const SendButton = styled.button`
  background: #102638;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;

  &:hover {
    background: #1a3a52;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

type MessageInputProps = {
  onSend: (body: string) => void
  disabled?: boolean
  placeholder?: string
}

const MessageInput: React.FC<MessageInputProps> = ({ onSend, disabled = false, placeholder = 'Type a message...' }) => {
  const [value, setValue] = useState('')
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return

    onSend(trimmed)
    setValue('')
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto'
      textAreaRef.current.focus()
    }
  }, [value, disabled, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    // Auto-resize
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [])

  return (
    <InputContainer>
      <TextArea
        ref={textAreaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={1}
        disabled={disabled}
      />
      <SendButton onClick={handleSubmit} disabled={disabled || !value.trim()} aria-label="Send message">
        <Hugeicon name="sent" size={18} />
      </SendButton>
    </InputContainer>
  )
}

export default MessageInput
